import { AfterViewInit, Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { CompanyMapResult, SearchCompaniesMapParameters } from '@api-proxies';
import { CompanyService } from '@http/clients/company.service';
import { TabNames } from '@models/enums/TabNames';
import { AwaiterService } from '@services/awaiter.service';
import { FilterStateService } from '@services/filter-state.service';
import { MapService, PanelFieldConfig } from '@services/map.service';
import { ICardDetails, MapCardComponent } from '@shared/map-card/map-card.component';
import { CounterBadgeComponent, IconSanitizerService } from 'ngx-lib';
import { Subscription } from 'rxjs';
import { IFilterValueCompanies } from '../companies-filters/companies-filters.component';

@Component({
    selector: 'app-companies-map',
    templateUrl: './companies-map.component.html',
    styleUrls: ['./companies-map.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CounterBadgeComponent, MapCardComponent]
})
export class CompaniesMapComponent implements AfterViewInit, OnDestroy, OnInit {
    filters?: IFilterValueCompanies;
    companies: CompanyMapResult[] = [];
    searchConfig: SearchCompaniesMapParameters = {} as SearchCompaniesMapParameters;
    selectedMarkerData: ICardDetails[] = [];
    companiesCounter?: number;
    closeIcon?: SafeHtml;
    panelConfig: PanelFieldConfig = {
        mainInfo: [
            { label: 'Type', valueKey: 'companyTypeName' },
            { label: 'Building', valueKey: 'buildingName' }
        ],
        tooltip: [
            { label: 'Main Contact', valueKey: 'contactName' },
            { label: 'Account Info', valueKey: 'accountName' }
        ],
        routerLink: {
            base: '/companies-single',
            extraKeys: [TabNames[TabNames.Overview]]
        }
    };

    private readonly subscriptions = new Subscription();

    private readonly awaiter = inject(AwaiterService);
    private readonly mapService = inject(MapService);
    private readonly iconSanitizer = inject(IconSanitizerService);
    private readonly filterStateService = inject(FilterStateService<IFilterValueCompanies>);
    private readonly companyService = inject(CompanyService);

    ngOnInit(): void {
        this.mapService.setPanelConfig(this.panelConfig);
        this.closeIcon = this.iconSanitizer.getIcon('close');
    }

    async ngAfterViewInit(): Promise<void> {
        await this.mapService.initializeMap('companies');

        const markerSub = this.mapService.panelData$.subscribe(({ items, count }) => {
            this.selectedMarkerData = items;
            this.companiesCounter = count;
        });

        const refreshSub = this.mapService.refresh$.subscribe(() => this.refreshMapView());

        const filterSub = this.filterStateService.stateFilters$.subscribe(async (newFilters) => {
            this.filters = { ...newFilters };

            this.closePanel();
            this.companies = [];

            this.refreshMapView();
        });

        this.subscriptions.add(refreshSub);
        this.subscriptions.add(markerSub);
        this.subscriptions.add(filterSub);
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    closePanel(): void {
        this.selectedMarkerData = [];
    }

    async refreshMapView(): Promise<void> {
        this.buildSearchConfig();
        await this.awaiter.awaitAction('Loading Companies', async () => {
            this.companies = await this.companyService.searchMap(this.searchConfig);
            this.mapService.fetchData(this.companies, 'companies-data');
        });
    }

    private buildSearchConfig(): void {
        const bounds = this.mapService.getMapBounds();
        if (!bounds) return;

        this.searchConfig = {
            latitudeFrom: bounds.getSouth(),
            latitudeTo: bounds.getNorth(),
            longitudeFrom: bounds.getWest(),
            longitudeTo: bounds.getEast(),
            filterText: this.filters?.searchText ?? undefined,
            contactId: this.filters?.contact?.id ?? undefined,
            companyTypeId: this.filters?.companyType?.id ?? undefined,
            marketId: this.filters?.market?.id ?? undefined,
            organizationId: undefined,
            includeArchived: this.filters?.includeArchived ?? undefined,
            salespersons: this.filters?.salespersons?.length
                ? this.filters?.salespersons
                      .map((s) => s.externalId)
                      .filter((externalId): externalId is number => externalId !== undefined)
                : undefined
        } as SearchCompaniesMapParameters;
    }
}
